// ============================================================================
// page-projects / project-list.js
// ----------------------------------------------------------------------------
// Client-side projects archive — List.js list + filters + GSAP card reveal.
//
// Globals expected from inline PHP (code block #218):
//   window.projects        Array of project objects.
//   window.projectPageUrl  URL of the projects archive. Reset target.
//   window.TaxonomyTerm    Optional. On a term archive page, the taxonomy
//                          slug (e.g. "architecture-styles"). Absent on the
//                          main /projects/ page.
//   window.TaxonomyName    Optional. The current term slug
//                          (e.g. "biophilic-design").
//
// Behaviour:
//   - Site area Min/Max inputs filter the list in-place via List.js.
//   - Reset Filter navigates back to `window.projectPageUrl`.
//   - On a term archive, the matching taxonomy filter is pre-applied so
//     the visitor sees only projects in that term, with the matching
//     select-item marked `.active`.
//   - Cards reveal with a stagger on scroll into view (ScrollTrigger).
// ============================================================================

import List from "list.js";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

document.addEventListener("DOMContentLoaded", function () {
  const wrap = document.getElementById("page--projects--project-list--wrap");
  const container = document.querySelector(
    ".page--projects--project-list--container",
  );
  if (!wrap || !container) return;

  const projects = Array.isArray(window.projects) ? window.projects : [];
  const projectPageUrl = window.projectPageUrl || "/projects/";

  // PHP pre-renders the list; throw it away and let List.js own the DOM.
  container.innerHTML = "";

  const options = {
    item: function (values) {
      return (
        '<div class="page--projects--project-list--item dh-container">' +
        '<img class="page--projects--project-list--img" src="' +
        values.img +
        '" alt="' +
        (values.img_alt || values.title) +
        '" />' +
        '<a href="' +
        values.link +
        '" class="page--projects--project-list--overlay dh-overlay">' +
        '<h2 class="page--projects--project-list--title">' +
        values.title +
        "</h2>" +
        "</a>" +
        "</div>"
      );
    },
    pagination: false,
  };

  const hackerList = new List(
    "page--projects--project-list--wrap",
    options,
    projects,
  );

  const filterAttr = {
    building: null,
    architecture_style: null,
    site_area_attr: { min: null, max: null },
    industry: null,
  };

  // --- Area range inputs --------------------------------------------------
  const minEl = document.querySelector("#min.projects__filter-area-input");
  if (minEl) {
    minEl.addEventListener("input", function (e) {
      filterAttr.site_area_attr.min = e.target.value ? e.target.value : null;
      onFilterChange();
    });
  }

  const maxEl = document.querySelector("#max.projects__filter-area-input");
  if (maxEl) {
    maxEl.addEventListener("input", function (e) {
      filterAttr.site_area_attr.max = e.target.value ? e.target.value : null;
      onFilterChange();
    });
  }

  // --- Reset button -------------------------------------------------------
  const resetButton = document.querySelector(".projects__filter-reset");
  if (resetButton) {
    resetButton.addEventListener("click", function () {
      window.location.href = projectPageUrl;
    });
  }

  function isFilterActive() {
    return (
      filterAttr.building !== null ||
      filterAttr.architecture_style !== null ||
      filterAttr.site_area_attr.min !== null ||
      filterAttr.site_area_attr.max !== null ||
      filterAttr.industry !== null
    );
  }

  function onFilterChange() {
    if (resetButton) {
      resetButton.style.display = isFilterActive() ? "flex" : "none";
    }

    hackerList.filter(function (item) {
      const attr = item.values();
      const buildingMatch =
        !filterAttr.building ||
        (attr.building && attr.building.slug === filterAttr.building);
      const styleMatch =
        !filterAttr.architecture_style ||
        (attr.architecture_style &&
          attr.architecture_style.slug === filterAttr.architecture_style);
      const minMatch =
        !filterAttr.site_area_attr.min ||
        Number(attr.site_area_attr) >= Number(filterAttr.site_area_attr.min);
      const maxMatch =
        !filterAttr.site_area_attr.max ||
        Number(attr.site_area_attr) <= Number(filterAttr.site_area_attr.max);
      const industryMatch =
        !filterAttr.industry ||
        (attr.industry && attr.industry.slug === filterAttr.industry);
      return (
        buildingMatch && styleMatch && minMatch && maxMatch && industryMatch
      );
    });
  }

  // --- Taxonomy archive auto-filter --------------------------------------
  // On a term archive (e.g. /architecture-styles/biophilic-design/), PHP
  // exposes the current taxonomy + term as globals. Pre-apply the matching
  // filter so visitors don't see unrelated projects.
  const taxonomyTerm =
    typeof window.TaxonomyTerm === "string" ? window.TaxonomyTerm : null;
  const taxonomyName =
    typeof window.TaxonomyName === "string" ? window.TaxonomyName : null;

  if (taxonomyTerm && taxonomyName) {
    if (taxonomyTerm === "building-type") {
      filterAttr.building = taxonomyName;
    } else if (taxonomyTerm === "architecture-styles") {
      filterAttr.architecture_style = taxonomyName;
    } else if (taxonomyTerm === "industry-type") {
      filterAttr.industry = taxonomyName;
    }

    const selectItem = document.querySelector(
      '.projects__filter-select-item[value="' + taxonomyName + '"]',
    );
    if (selectItem) selectItem.classList.add("active");

    onFilterChange();
  }

  // --- Card reveal animation ---------------------------------------------
  ScrollTrigger.batch(".page--projects--project-list--item", {
    start: "top 85%",
    once: true,
    onEnter: function (batch) {
      gsap.from(batch, {
        duration: 1,
        stagger: 0.1,
        opacity: 0,
        y: 100,
        ease: "power1.out",
        overwrite: "auto",
      });
    },
  });
});
